/**
 * Display helpers for the sticking-patterns views.
 *
 * **This screen shows the stored values verbatim.** Everything on it comes from
 * one place — the `diagnosis` object the analyzer emitted, projected into
 * `user-memory` — and an admin looking at it is usually asking what the model
 * actually produced. A friendlier rendering here answers a question nobody was
 * asking and hides the one they were.
 *
 * It used to prettify: a `CATEGORY_LABELS` map turned `calculation_slip` into
 * "Calculation slips", falling back to Title Case for anything unmapped. Seven
 * of its ten entries (`concept_confusion`, `method_error`, `unit_error`,
 * `sign_error`, …) were not in the diagnosis vocabulary at all, and four terms
 * that ARE (`recall_gap`, `application_error`, `analysis_error`,
 * `evaluation_gap`) had no entry — so the page showed "Calculation slips" beside
 * "Recall Gap" and neither matched what was stored.
 *
 * The student app keeps its own labels (`patternLabels.ts`), which is the right
 * place for them: a student is owed readable language, an admin is owed the
 * data.
 *
 * See docs/FeatureDesigns/diagnosis/sticking-patterns-surfaces.md.
 */

export function trendLabel(trend?: string): string {
  // Stored verbatim — `closing` used to render as "improving", which is an
  // interpretation, and the one term an admin most needs to match against the
  // code that computes it.
  return trend ?? '—';
}

/** `Jul 22, 14:22` — same convention as the other dense admin tables. */
export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date}, ${time}`;
}

/** "5 persistent · 2 new" — how a stored enum splits across the carriers of one
 *  pattern. Used for both `trend` and `lastKind`; the values are printed as
 *  stored. */
export function spread(counts: Record<string, number>): string {
  const parts = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([value, n]) => `${n} ${value}`);
  return parts.length > 0 ? parts.join(' · ') : '—';
}
